import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { stripHtmlComments } from '../utils/markdownText';

export default function MarkdownRenderer({ markdown, assets = {}, darkMode = false }) {
  const components = {
    table({ children }) {
      return (
        // Border off the darkMode PROP, not a `dark:` variant: the variant reads a
        // `.dark` ancestor, so a light-forced surface inside a dark window (the
        // field-insight sidebar in a report popup) drew a dark border on a white
        // card. Every other colour here is already prop-driven.
        <div className={`my-4 overflow-x-auto rounded-xl border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="min-w-full border-collapse text-sm">{children}</table>
        </div>
      );
    },
    thead({ children }) {
      return <thead className={darkMode ? 'bg-[#2b2b2b]' : 'bg-gray-50'}>{children}</thead>;
    },
    th({ children }) {
      return (
        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className={`px-4 py-3 align-top ${darkMode ? 'border-gray-700 text-gray-200' : 'border-gray-100 text-gray-800'}`}>
          {children}
        </td>
      );
    },
    tr({ children }) {
      return <tr className={darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}>{children}</tr>;
    },
    code() { return null; },
    pre() { return null; }
  };

  const renderSegment = (segment, idx) => {
    const plotMatch = segment.match(/^\[PLOT:([^\]]+)\]$/i);
    if (plotMatch) {
      const id = plotMatch[1];
      const plots = assets?.plots || {};
      const plot = plots[id];
      if (plot?.data) {
        return (
          <div key={`plot-${id}-${idx}`} className="my-4">
            {plot.caption && <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{plot.caption}</div>}
            <img src={plot.data} alt={plot.caption || id} className="max-w-full h-auto rounded-md" />
          </div>
        );
      }
      return (
        <div key={`plot-missing-${id}-${idx}`} className={`border rounded p-3 my-4 ${darkMode ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
          Chart unavailable ({id})
        </div>
      );
    }
    return (
      <ReactMarkdown key={idx} remarkPlugins={[remarkGfm]} components={components}>
        {segment}
      </ReactMarkdown>
    );
  };

  // Explicitly, not incidentally: see utils/markdownText.
  const visible = stripHtmlComments(markdown);
  if (!visible) return null;

  const segments = visible
    .split(/(\[PLOT:[^\]]+\])/gi)
    .filter(Boolean);

  return <div>{segments.map((segment, idx) => renderSegment(segment, idx))}</div>;
}


